import {
    ValidationResult,
    ValidationFailure,
    createFailure,
    FailureSeverity,
    validationSuccessful,
    createValidationResult
} from "sprint-common-web";

import { gpacTokens } from "../../common/gpacTokens";

interface CommandsCorrelationValidationRequest {
    command: string;
    subtitlesCommand?: string;
}

export const useGpacCommandValidation = () => {
    const validateCommandsCorrelation = ({
        command,
        subtitlesCommand
    }: CommandsCorrelationValidationRequest): ValidationResult => {
        const failures: ValidationFailure[] = [];

        if (!command?.includes(gpacTokens.SUBTITLES_COMMAND_TOKEN) && subtitlesCommand) {
            const warning = createFailure(
                "Subtitles command placeholder is missing in command",
                "subtitlesCommand",
                FailureSeverity.Warning
            );

            failures.push(warning);
        }

        if (command?.includes(gpacTokens.SUBTITLES_COMMAND_TOKEN) && subtitlesCommand) {
            return validationSuccessful();
        }

        if (command?.includes(gpacTokens.SUBTITLES_COMMAND_TOKEN) && !subtitlesCommand) {
            const failure = createFailure("Subtitles command is missing", "subtitlesCommand");

            failures.push(failure);
        }

        return createValidationResult(failures);
    };

    const validateCommand = (command: string): ValidationResult => {
        const failures: ValidationFailure[] = [];
        if (!command.includes(gpacTokens.SOUNDTRACKS_COMMAND_TOKEN)) {
            const failure = createFailure(`Command must contain ${gpacTokens.SOUNDTRACKS_COMMAND_TOKEN}`, "command");

            failures.push(failure);
        }

        const validPlaceholders = [
            gpacTokens.SUBTITLES_COMMAND_TOKEN,
            gpacTokens.SOUNDTRACKS_COMMAND_TOKEN,
            gpacTokens.VIDEO_FILE_PATH
        ];

        const incorrectPlaceholders = findIncorrectPlaceholders(command, validPlaceholders);

        if (incorrectPlaceholders.length) {
            const failure = createFailure(`Command cannot contain '${incorrectPlaceholders.join("', '")}'`, "command");

            failures.push(failure);
        }

        return createValidationResult(failures);
    };

    const validateSoundtracksCommand = (soundtracksCommand: string): ValidationResult => {
        if (!soundtracksCommand) {
            return validationSuccessful();
        }

        const failures: ValidationFailure[] = [];

        if (!soundtracksCommand.includes(gpacTokens.SOUNDTRACKS_FILE_PATH_TOKEN)) {
            const failure = createFailure(
                `Soundtracks command must contain ${gpacTokens.SOUNDTRACKS_FILE_PATH_TOKEN}`,
                "soundtracksCommand"
            );

            failures.push(failure);
        }

        if (
            !soundtracksCommand.includes(gpacTokens.LANGUAGE_ISO639_TOKEN) &&
            !soundtracksCommand.includes(gpacTokens.LANGUAGE_LARG_TOKEN) &&
            !soundtracksCommand.includes(gpacTokens.LANGUAGE_SPECIFICATION_TOKEN)
        ) {
            const failure = createFailure(
                `Soundtracks command must contain ${gpacTokens.LANGUAGE_ISO639_TOKEN} or ${gpacTokens.LANGUAGE_LARG_TOKEN}`,
                "soundtracksCommand"
            );

            failures.push(failure);
        }

        const validPlaceholders = [
            gpacTokens.SOUNDTRACKS_FILE_PATH_TOKEN,
            gpacTokens.LANGUAGE_ISO639_TOKEN,
            gpacTokens.LANGUAGE_LARG_TOKEN,
            gpacTokens.LANGUAGE_SPECIFICATION_TOKEN
        ];

        const incorrectPlaceholders = findIncorrectPlaceholders(soundtracksCommand, validPlaceholders);

        if (incorrectPlaceholders.length) {
            const failure = createFailure(
                `Soundtracks command cannot contain '${incorrectPlaceholders.join("', '")}'`,
                "soundtracksCommand"
            );

            failures.push(failure);
        }

        return createValidationResult(failures);
    };

    const validateSubtitlesCommand = (subtitlesCommand: string): ValidationResult => {
        if (!subtitlesCommand) {
            return validationSuccessful();
        }

        const failures: ValidationFailure[] = [];

        if (!subtitlesCommand.includes(gpacTokens.SUBTITLES_FILE_PATH_TOKEN)) {
            const failure = createFailure(
                `Subtitles command must contain ${gpacTokens.SUBTITLES_FILE_PATH_TOKEN}`,
                "subtitlesCommand"
            );

            failures.push(failure);
        }

        if (
            !subtitlesCommand.includes(gpacTokens.LANGUAGE_ISO639_TOKEN) &&
            !subtitlesCommand.includes(gpacTokens.LANGUAGE_LARG_TOKEN) &&
            !subtitlesCommand.includes(gpacTokens.LANGUAGE_SPECIFICATION_TOKEN)
        ) {
            const failure = createFailure(
                `Subtitles command must contain ${gpacTokens.LANGUAGE_ISO639_TOKEN} or ${gpacTokens.LANGUAGE_LARG_TOKEN}`,
                "subtitlesCommand"
            );

            failures.push(failure);
        }

        const validPlaceholders = [
            gpacTokens.SUBTITLES_FILE_PATH_TOKEN,
            gpacTokens.LANGUAGE_ISO639_TOKEN,
            gpacTokens.LANGUAGE_LARG_TOKEN,
            gpacTokens.LANGUAGE_SPECIFICATION_TOKEN
        ];

        const incorrectPlaceholders = findIncorrectPlaceholders(subtitlesCommand, validPlaceholders);

        if (incorrectPlaceholders.length) {
            const failure = createFailure(
                `Subtitles command cannot contain '${incorrectPlaceholders.join("', '")}'`,
                "subtitlesCommand"
            );

            failures.push(failure);
        }

        return createValidationResult(failures);
    };

    return {
        validateCommand,
        validateSoundtracksCommand,
        validateSubtitlesCommand,
        validateCommandsCorrelation
    };
};

const findIncorrectPlaceholders = (command: string, validPlaceholders: string[]): string[] => {
    const placeholders = command.match(/{[^}]*}/g) ?? [];

    return placeholders.filter((item) => !validPlaceholders.includes(item));
};
